import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { useCatalogCanonicalsPaged, useCatalogFilterOptions } from '@/hooks/useCatalog';
import { LinkedViewsStrip } from '@/components/catalog/LinkedViewsStrip';
import { StagePill } from '@/components/catalog/StagePill';
import { LanguageCoverage } from '@/components/catalog/LanguageCoverage';
import { SOURCE_LABELS } from '@/components/catalog/sourceLabels';
import { CatalogFilterChips, FilterChip } from '@/components/catalog/CatalogFilterChips';
import { UrlStateBadge } from '@/components/catalog/UrlStateBadge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertCircle, Link as LinkIcon, Package,
  ChevronLeft, ChevronRight, Filter,
} from 'lucide-react';
import { catalogColors } from '@/components/catalog/catalogColors';
import { formatDistanceToNow } from 'date-fns';

const PAGE_SIZE = 50;

// All 8 filter dimensions exposed as chips
const FILTER_CHIPS: FilterChip[] = [
  { dim: 'category',                    label: 'Category' },
  { dim: 'custom_label_4',              label: 'Custom label 4' },
  { dim: 'brand',                       label: 'Brand' },
  { dim: 'source',                      label: 'Source' },
  { dim: 'aeo_status',                  label: 'AEO status' },
  { dim: 'live_status',                 label: 'Live status' },
  { dim: 'translation_coverage_bucket', label: 'Translation coverage' },
  { dim: 'updated_bucket',              label: 'Updated' },
];

function relTime(iso: string | null) {
  if (!iso) return '—';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return '—'; }
}

// Thumbnail — matches /content-review style. Renders nothing when src is missing
// or when the image fails to load (no placeholder, no background).
function Thumbnail({ src, alt }: { src?: string | null; alt: string }) {
  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-12 h-12 object-contain rounded shrink-0"
      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
    />
  );
}

// Cell with tooltip for truncated content
function TruncCell({ text, maxWidth = 220 }: { text: string; maxWidth?: number }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="truncate" style={{ maxWidth }}>{text}</div>
        </TooltipTrigger>
        <TooltipContent className="max-w-md">
          <p className="text-xs break-all">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function CatalogMonitor() {
  const { selectedSite } = useSite();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const siteId = selectedSite?.id || selectedSite?._id;
  const sort   = params.get('s') || 'updated_at:desc';
  const search = params.get('q') || '';
  const pageParam = Number(params.get('page') || '1');
  const page = Number.isFinite(pageParam) && pageParam >= 1 ? Math.floor(pageParam) : 1;

  // Collect filter[dim]=value from URL — memoized so the queryKey ref is stable
  // across renders. Without this, every render rebuilds the object and TanStack
  // Query treats it as a new cache key → refetches + flicker.
  const filters = useMemo(() => {
    const out: Record<string, string> = {};
    // Iterate keys sorted so the object insertion order is deterministic
    const keys = Array.from(params.keys()).sort();
    for (const k of keys) {
      if (k.startsWith('f.')) out[k.slice(2)] = params.get(k) ?? '';
    }
    if (search) out['q'] = search;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString(), search]);

  const { data, isLoading, isFetching, error } =
    useCatalogCanonicalsPaged(siteId, filters, sort, page, PAGE_SIZE);
  const { data: filterOptions } = useCatalogFilterOptions(siteId);

  const rows = data?.rows ?? [];
  const totalEstimate = data?.total_estimate ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalEstimate / PAGE_SIZE));

  // Clamp page if filters reduced the count below current page.
  // Guard with !isFetching so we don't clamp during the brief transient when
  // totalPages reflects the previous query while the new query is still loading.
  useEffect(() => {
    if (!isFetching && page > totalPages && totalPages > 0) {
      setParams(prev => { const p = new URLSearchParams(prev); p.delete('page'); return p; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages, page, isFetching]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const setPage = useCallback((n: number) => {
    setParams(prev => {
      const p = new URLSearchParams(prev);
      if (n <= 1) p.delete('page'); else p.set('page', String(n));
      return p;
    }, { replace: true });
  }, [setParams]);

  const setFilterValue = useCallback((dim: string, val: string) => {
    setParams(prev => {
      const p = new URLSearchParams(prev);
      p.set(`f.${dim}`, val);
      p.delete('page');
      return p;
    });
  }, [setParams]);

  const clearFilter = useCallback((dim: string) => {
    setParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete(`f.${dim}`);
      p.delete('page');
      return p;
    });
  }, [setParams]);

  const clearAllFilters = useCallback(() => {
    setParams(prev => {
      const p = new URLSearchParams();
      const sortVal = prev.get('s'); if (sortVal) p.set('s', sortVal);
      const q       = prev.get('q'); if (q)       p.set('q', q);
      return p;
    });
  }, [setParams]);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setParams(prev => {
      const p = new URLSearchParams(prev);
      if (val) p.set('q', val); else p.delete('q');
      p.delete('page');
      return p;
    }, { replace: true });
  }, [setParams]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  const rangeStart = totalEstimate > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd   = Math.min(page * PAGE_SIZE, totalEstimate);

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Catalog Monitor</h1>
          <p className="text-muted-foreground mt-1">
            {totalEstimate > 0
              ? <><span className="font-semibold text-foreground tabular-nums">{totalEstimate.toLocaleString()}</span> products matching filters</>
              : 'Row-level view of all canonical products'}
          </p>
        </div>
        <LinkedViewsStrip />
      </div>

      {/* Filters card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Input
              placeholder="Search product name, ID, brand…"
              defaultValue={search}
              onChange={handleSearch}
              className="h-9 w-80 text-sm"
            />
            <UrlStateBadge />
          </div>
          <CatalogFilterChips
            chips={FILTER_CHIPS}
            activeFilters={filters}
            options={filterOptions}
            onSet={setFilterValue}
            onClear={clearFilter}
            onClearAll={clearAllFilters}
          />
        </CardContent>
      </Card>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive p-3 rounded-lg bg-destructive/10">
          <AlertCircle className="h-4 w-4 shrink-0" />{(error as Error).message}
        </div>
      )}

      {!siteId && !isLoading && (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">Select a site.</CardContent>
        </Card>
      )}

      {isLoading && (
        <Card>
          <CardContent className="py-4 space-y-2">
            {Array.from({length: 8}).map((_,i) => <Skeleton key={i} className="h-12 rounded" />)}
          </CardContent>
        </Card>
      )}

      {!isLoading && siteId && rows.length > 0 && (
        <div className="space-y-4">
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[64px]"></TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead className="w-[180px]">Category</TableHead>
                  <TableHead className="w-[160px]">Source</TableHead>
                  <TableHead className="w-[130px]">AEO generated</TableHead>
                  <TableHead className="w-[100px]">Languages</TableHead>
                  <TableHead className="w-[80px]">Live</TableHead>
                  <TableHead className="w-[120px]">Updated</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.pfi_id}>
                    <TableCell>
                      <Thumbnail src={row.image_url} alt={row.product_name} />
                    </TableCell>
                    <TableCell className="max-w-[240px]">
                      <TruncCell text={row.product_name} maxWidth={220} />
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">{row.product_id}</div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[180px]">
                      <TruncCell text={row.category} maxWidth={160} />
                    </TableCell>
                    <TableCell>
                      <span
                        className="cursor-pointer"
                        onClick={() => setFilterValue('source', row.source)}
                        title="Filter by source"
                      >
                        <StagePill state={row.source} label={SOURCE_LABELS[row.source] ?? row.source} />
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className="cursor-pointer"
                        onClick={() => row.aeo_status && setFilterValue('aeo_status', row.aeo_status)}
                      >
                        {row.aeo_status
                          ? <StagePill state={row.aeo_status} />
                          : <StagePill state="not_generated" />}
                      </span>
                    </TableCell>
                    <TableCell>
                      <LanguageCoverage count={row.completed_languages} total={17} />
                    </TableCell>
                    <TableCell>
                      {row.is_live_on_site
                        ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: catalogColors.purple + '20', color: catalogColors.purple }}>Live</span>
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{relTime(row.updated_at)}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7" title="Inspect URL"
                        onClick={() => navigate(`/catalog/inspect?q=${encodeURIComponent(row.product_url || row.product_id)}`)}
                      >
                        <LinkIcon className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination — content-review pattern */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {rangeStart.toLocaleString()} to {rangeEnd.toLocaleString()} of {totalEstimate.toLocaleString()} products
                {isFetching && <span className="ml-2 italic">Loading…</span>}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!isLoading && !error && siteId && rows.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <Package className="h-8 w-8 mx-auto mb-2 opacity-30" />
            No products match the current filters.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
